// @ts-nocheck
import { Trans } from '@lingui/react'
import type { CartDiscountsV2PromotionTypeEnum } from '@redteclab/api/clients/bully'
import { clsx } from 'clsx'
import { type Dispatch, type SetStateAction, type VFC } from 'react'

import { ENTRY_TYPE } from 'shared/consts/checkout'
import {
  CART_FREE_GIFT_PROMOTION,
  EXPERIMENT_VARIATION,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { type CartControllerGroupEntryV2 } from 'types/api/cart'
import { DeviceClass } from 'types/shopConfig'
import freebieBadge from 'views/assets/images/icons/IconPresent.svg'
import Image from 'views/components/atoms/Image'

import BadgeContainer from './BadgeContainer'
import PriceBox from './PriceBox'
import ProductListingDeleteButton from './ProductListingDeleteButton'
import ProductListingSelect from './ProductListingSelect'

interface ProductItemContentProps {
  product: CartControllerGroupEntryV2
  currency?: string
  disabledSelect?: boolean
  isFreebie?: boolean
  deviceClass: DeviceClass
  isERx: boolean
  hasOverdraft: boolean
  hasOverdraftMaxed: boolean
  evaluatedProductType: string
  setGreyedOut: Dispatch<SetStateAction<never[]>>
  discountBoundaries: number
  promotions?: Record<CartDiscountsV2PromotionTypeEnum, string[]>
}

const ProductItemContent: VFC<ProductItemContentProps> = ({
  product,
  isERx,
  isFreebie,
  deviceClass,
  disabledSelect,
  hasOverdraft,
  hasOverdraftMaxed,
  evaluatedProductType,
  setGreyedOut,
  discountBoundaries,
  currency,
  promotions,
}) => {
  const cartFreeGiftPromotionExperiment = useGetExperiment(
    useGetExperimentNamePerTenant(CART_FREE_GIFT_PROMOTION)
  )

  return (
    <>
      {product.entryType !== ENTRY_TYPE.SERVICE && (
        <div className="l-flex l-flex--distribute-space l-flex__row_reverse@medium u-margin--bottom u-margin-large--right@medium u-no-margin--bottom@medium o-ProductListing--quantity o-ProductListing--quantity@medium">
          {(!cartFreeGiftPromotionExperiment?.isEnabled ||
            cartFreeGiftPromotionExperiment?.variant ===
              EXPERIMENT_VARIATION.DEFAULT ||
            !isFreebie) && (
            <div
              className="l-flex l-flex__grow-one o-ProductListing--select@medium"
              id={`offer_select_${product.offerId}`}
            >
              <ProductListingSelect
                product={product}
                isERx={isERx}
                disabledSelect={disabledSelect}
                hasOverdraft={hasOverdraft}
                hasOverdraftMaxed={hasOverdraftMaxed}
                evaluatedProductType={evaluatedProductType}
              />
            </div>
          )}
          {!isFreebie && (
            <div className="l-flex u-margin--right@medium o-ProductListing__delete-button">
              <ProductListingDeleteButton
                isERx={isERx}
                product={product}
                evaluatedProductType={evaluatedProductType}
                setGreyedOut={setGreyedOut}
              />
            </div>
          )}
        </div>
      )}
      <div className="l-flex l-flex__fill-gap u-margin-large--left@medium o-ProductListing__price-badge">
        <div
          className={clsx(
            'l-flex l-flex--justify-content--end@medium u-margin-x-large--right@medium o-ProductListing__badgeContainer',
            {
              'l-flex--distribute-space':
                cartFreeGiftPromotionExperiment?.isEnabled && isFreebie,
              'l-flex__half-width': !isFreebie,
              'u-hidden@small-tiny':
                cartFreeGiftPromotionExperiment?.variant ===
                EXPERIMENT_VARIATION.V2,
            }
          )}
        >
          <BadgeContainer
            product={product}
            discountBoundaries={discountBoundaries}
            promotions={promotions}
            prescriptionType={isERx ? 'erxBonus' : 'rxBonus'}
          />
        </div>

        {cartFreeGiftPromotionExperiment?.variant === EXPERIMENT_VARIATION.V2 &&
          isFreebie && (
            <div className="l-flex l-flex--distribute-space l-flex--justify-content--end@medium u-margin-x-large--right@medium u-color--grey">
              <p className="u-margin-large--right u-no-wrap">
                <Trans id="general.label.quantity" />: {product.quantity}
              </p>
              <Image src={freebieBadge} alt="freebieBadge" />
            </div>
          )}

        <div
          className={clsx(
            'l-flex l-flex--end l-flex--vertical-center fixedWidthPriceBox',
            {
              'l-flex--distribute-space':
                cartFreeGiftPromotionExperiment?.isEnabled && isFreebie,
              'u-margin-x-large--left':
                cartFreeGiftPromotionExperiment?.variant ===
                  EXPERIMENT_VARIATION.V2 &&
                isMobile(deviceClass) &&
                isFreebie,
              'u-margin-x-large--left@medium':
                !cartFreeGiftPromotionExperiment?.isEnabled,
              'l-flex__half-width': !isFreebie,
            }
          )}
        >
          {cartFreeGiftPromotionExperiment?.isEnabled &&
            ((cartFreeGiftPromotionExperiment?.variant ===
              EXPERIMENT_VARIATION.V1 &&
              !isMobile(deviceClass)) ||
              cartFreeGiftPromotionExperiment?.variant ===
                EXPERIMENT_VARIATION.V2) &&
            isFreebie && (
              <p
                className={clsx(
                  'o-ProductListing__freeInformation u-font-style--italic',
                  {
                    'u-color--primary u-font-weight-rebranding--medium':
                      cartFreeGiftPromotionExperiment?.variant ===
                      EXPERIMENT_VARIATION.V1,
                    'u-color--grey u-font-weight--bold':
                      cartFreeGiftPromotionExperiment?.variant ===
                      EXPERIMENT_VARIATION.V2,
                  }
                )}
              >
                <Trans id="product-listing.section.free" />
              </p>
            )}
          {(!cartFreeGiftPromotionExperiment?.isEnabled ||
            cartFreeGiftPromotionExperiment?.variant ===
              EXPERIMENT_VARIATION.DEFAULT ||
            !isFreebie) &&
            !product.priceLabelInfo && (
              <PriceBox
                isERx={isERx}
                product={product}
                currency={currency}
                evaluatedProductType={evaluatedProductType}
                promotions={promotions}
              />
            )}
        </div>
      </div>
    </>
  )
}

export default ProductItemContent
